import React, { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const HarinaamBulkSchedule = ({ isOpen, onClose, onSave }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedDates, setSelectedDates] = useState([]);

  const [scheduleData, setScheduleData] = useState([]);
  const [error, setError] = useState("");

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

  const formatDisplayDate = (day, month, year) => {
    const short = monthNames[month].slice(0, 3);
    return `${short} ${day}, ${year}`;
  };

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setCurrentMonth(new Date().getMonth());
      setCurrentYear(new Date().getFullYear());
      setSelectedDates([]);
      setScheduleData([]);
      setError("");
    }
  }, [isOpen]);

  const handlePrevMonth = () => {
    setError("");
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else setCurrentMonth((m) => m - 1);
  };

  const handleNextMonth = () => {
    setError("");
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else setCurrentMonth((m) => m + 1);
  };

  // Add a row for a selected date
  const addScheduleRow = (day, month, year) => {
    const newRow = {
      id: `${year}-${month}-${day}-${Date.now()}-${Math.random()}`,
      day,
      month,
      year,
      time: "",
      location: "",
    };
    setScheduleData((rows) => [...rows, newRow]);
  };

  // Toggle date from calendar
  const toggleDateSelection = (day) => {
    setError("");

    setSelectedDates((prev) => {
      const exists = prev.some(
        (d) =>
          d.day === day && d.month === currentMonth && d.year === currentYear
      );

      if (exists) {
        // Remove date from both lists
        setScheduleData((rows) =>
          rows.filter(
            (r) =>
              !(
                r.day === day &&
                r.month === currentMonth &&
                r.year === currentYear
              )
          )
        );

        return prev.filter(
          (d) =>
            !(
              d.day === day &&
              d.month === currentMonth &&
              d.year === currentYear
            )
        );
      }

      // Add new schedule row
      addScheduleRow(day, currentMonth, currentYear);

      return [...prev, { day, month: currentMonth, year: currentYear }];
    });
  };

  const handleTimeChange = (id, value) => {
    setScheduleData((rows) =>
      rows.map((r) => (r.id === id ? { ...r, time: value } : r))
    );
  };

  const handleLocationChange = (id, value) => {
    setScheduleData((rows) =>
      rows.map((r) => (r.id === id ? { ...r, location: value } : r))
    );
  };

  const today = new Date();
  const todayDay = today.getDate();
  const todayMonth = today.getMonth();
  const todayYear = today.getFullYear();

  const renderCalendar = () => {
    const blanks = Array(firstDayOfMonth)
      .fill(null)
      .map((_, i) => <div key={`b-${i}`} />);

    const days = [];

    for (let d = 1; d <= daysInMonth; d++) {
      const isSelected = selectedDates.some(
        (sel) =>
          sel.day === d &&
          sel.month === currentMonth &&
          sel.year === currentYear
      );

      // Determine if this date is in the past
      const isPast =
        currentYear < todayYear ||
        (currentYear === todayYear && currentMonth < todayMonth) ||
        (currentYear === todayYear &&
          currentMonth === todayMonth &&
          d < todayDay);

      days.push(
        <div
          key={d}
          onClick={() => !isPast && toggleDateSelection(d)} // prevent click
          style={{
            textAlign: "center",
            padding: "8px",
            cursor: isPast ? "not-allowed" : "pointer",
            borderRadius: "6px",
            backgroundColor: isSelected ? "#b91c1c" : "transparent",
            color: isPast ? "#9ca3af" : isSelected ? "white" : "#374151",
            opacity: isPast ? 0.5 : 1,
            fontWeight: isSelected ? "600" : "400",
            transition: "0.2s",
            pointerEvents: isPast ? "none" : "auto", // fully disable interaction
          }}
        >
          {d}
        </div>
      );
    }

    return [...blanks, ...days];
  };

  const handleSubmit = () => {
    setError("");

    if (!scheduleData.length) {
      setError("Please select at least one date.");
      return;
    }

    const invalid = scheduleData.find((r) => !r.time || !r.location.trim());
    if (invalid) {
      setError("Please fill both Time and Location for all rows.");
      return;
    }

    const finalPayload = scheduleData
      .sort(
        (a, b) =>
          new Date(a.year, a.month, a.day) - new Date(b.year, b.month, b.day)
      )
      .map((r) => {
        const dateObj = new Date(r.year, r.month, r.day);
        const [HH, MM] = r.time.split(":");

        dateObj.setHours(HH);
        dateObj.setMinutes(MM);
        dateObj.setSeconds(0);

        const yyyy = dateObj.getFullYear();
        const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
        const dd = String(dateObj.getDate()).padStart(2, "0");
        const hh = String(dateObj.getHours()).padStart(2, "0");
        const min = String(dateObj.getMinutes()).padStart(2, "0");

        return {
          date_time: `${yyyy}-${mm}-${dd} ${hh}:${min}:00`,
          location: r.location,
        };
      });

    onSave(finalPayload);
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
          zIndex: 9999,
        }}
      >
        <div
          style={{
            background: "white",
            width: "100%",
            maxWidth: 1200,
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "16px 24px",
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, textAlign: "center" }}>
              Bulk Schedule Your Next Harinaam Sankirtan
            </h2>
            <button
              onClick={onClose}
              style={{ border: "none", background: "none", cursor: "pointer" }}
            >
              <X size={24} color="#9ca3af" />
            </button>
          </div>

          {/* MAIN CONTENT WRAPPER */}
          <div className="schedule-wrapper">
            {/* LEFT CALENDAR */}
            <div className="schedule-left">
              <h3 style={{ fontSize: 16, marginBottom: 16 }}>
                Select Date Range
              </h3>

              <div
                style={{
                  border: "1px solid #ddd",
                  padding: 16,
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 16,
                  }}
                >
                  <button
                    onClick={handlePrevMonth}
                    style={{
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                    }}
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div style={{ fontWeight: 600 }}>
                    {monthNames[currentMonth]} {currentYear}
                  </div>

                  <button
                    onClick={handleNextMonth}
                    style={{
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                    }}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7,1fr)",
                    gap: 6,
                  }}
                >
                  {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map(
                    (d) => (
                      <div
                        key={d}
                        style={{
                          textAlign: "center",
                          fontSize: 11,
                          color: "#666",
                        }}
                      >
                        {d}
                      </div>
                    )
                  )}
                </div>

                <div className="schedule-calendar-container">
                  <div
                    className="schedule-calendar-grid"
                    style={{
                      marginTop: 8,
                      display: "grid",
                      gridTemplateColumns: "repeat(7,1fr)",
                      gap: 1,
                    }}
                  >
                    {renderCalendar()}
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT SIDE LIST */}
            <div className="schedule-right">
              <div className="schedule-header">
                <div>Date & Time</div>
                <div>Location</div>
                <div>Add</div>
              </div>

              {scheduleData
                .sort(
                  (a, b) =>
                    new Date(a.year, a.month, a.day) -
                    new Date(b.year, b.month, b.day)
                )
                .map((item) => {
                  const rowsForDate = scheduleData.filter(
                    (r) =>
                      r.day === item.day &&
                      r.month === item.month &&
                      r.year === item.year
                  );

                  return (
                    <div key={item.id} className="schedule-row">
                      {/* Date + Time */}
                      <div className="date-time-group">
                        <div className="date-display">
                          {formatDisplayDate(item.day, item.month, item.year)}
                        </div>

                        <input
                          type="time"
                          value={item.time}
                          onChange={(e) =>
                            handleTimeChange(item.id, e.target.value)
                          }
                          className="time-input"
                        />
                      </div>

                      {/* Location */}
                      <input
                        type="text"
                        value={item.location}
                        onChange={(e) =>
                          handleLocationChange(item.id, e.target.value)
                        }
                        className="location-input"
                        placeholder="Enter location"
                      />

                      {/* Buttons */}
                      <div className="action-buttons">
                        <button
                          onClick={() =>
                            addScheduleRow(item.day, item.month, item.year)
                          }
                          className="btn-circle"
                        >
                          +
                        </button>

                        {rowsForDate.length > 1 && (
                          <button
                            onClick={() =>
                              setScheduleData((rows) =>
                                rows.filter((r) => r.id !== item.id)
                              )
                            }
                            className="btn-circle"
                            style={{ color: "red", fontWeight: "bold" }}
                          >
                            –
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

              {error && (
                <div style={{ color: "red", marginTop: 10, fontSize: 13 }}>
                  {error}
                </div>
              )}

              <div
                style={{
                  textAlign: "right",
                  marginTop: 20,
                  paddingTop: 16,
                  borderTop: "1px solid #eee",
                }}
              >
                <button
                  onClick={handleSubmit}
                  style={{
                    background: "#b91c1c",
                    color: "white",
                    padding: "10px 32px",
                    borderRadius: 6,
                    border: "none",
                    cursor: "pointer",
                    fontSize: 14,
                  }}
                >
                  Schedule Sankirtan
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HarinaamBulkSchedule;
